// contexts/workflow-context.js
import { createContext, useContext, useState, useCallback } from 'react';
import { message } from 'antd';
import { postAuthenticatedRequest } from '../../../utils/api/requests/request';

const WorkflowContext = createContext();

export const WorkflowProvider = ({ children }) => {
    // Simple state management
    const [processInstances, setProcessInstances] = useState(null);
    const [instanceStates, setInstanceStates] = useState({});

    // Track last request parameters to avoid duplicate calls
    const [lastRequests, setLastRequests] = useState({
        instances: null,
        instanceState: null
    });

    // Loading states
    const [loading, setLoading] = useState({
        instances: false,
        instanceState: false,
        definition: false
    });

    // ==================== API FUNCTIONS ====================

    // Get process instances directly
    const getProcessInstances = useCallback(async (entityId, entityReferenceId, page = 1, pageSize = 10, forceRefresh = false) => {
        // Create unique key for this request
        const requestKey = `${entityReferenceId}-${entityId}-${page}-${pageSize}`;
        
        // Check if this is the same as last request and not forcing refresh
        if (!forceRefresh && lastRequests.instances === requestKey) {
            // Return existing data if available
            const existingData = processInstances[requestKey];
            if (existingData) {
                console.log(`Returning existing instances for ${requestKey}`);
                return existingData;
            }
        }

        try {
            setLoading(prev => ({ ...prev, instances: true }));
            
            // Update last request
            setLastRequests(prev => ({ ...prev, instances: requestKey }));

            const data = {
                filters: [
                    { "field": "EntityReferenceId", "operator": "equals", "value": entityReferenceId },
                    { "field": "Entity", "operator": "equals", "value": entityId },
                ],
                pageSize,
                pageIndex: page - 1,
            };

            const response = await postAuthenticatedRequest(
                "GetProcessInstanceListQuery",
                data
            );

            if (response?.ok && response?.outData?.isSuccessful) {
                const responseData = response.outData.data || [];
                const totalRows = response.outData.totalRows || 0;

                const result = {
                    instances: responseData,
                    totalRows,
                    page,
                    pageSize,
                };

                // Store directly in state
                setProcessInstances(prev => ({
                    ...prev,
                    [requestKey]: result
                }));

                return result;
            } else {
                throw new Error(response?.outData?.message ?? "Failed to fetch process instances");
            }
        } catch (error) {
            console.error('Error fetching process instances:', error);
            message.error(error.message);
            throw error;
        } finally {
            setLoading(prev => ({ ...prev, instances: false }));
        }
    }, [lastRequests, processInstances]);

    // Get process instance state directly
    const getProcessInstanceState = useCallback(async (instanceGuid, forceRefresh = false) => {
        // Check if this is the same as last request and not forcing refresh
        if (!forceRefresh && lastRequests.instanceState === instanceGuid) {
            // Return existing data if available
            const existingData = instanceStates[instanceGuid];
            if (existingData) {
                console.log(`Returning existing state for ${instanceGuid}`);
                return existingData;
            }
        }

        try {
            setLoading(prev => ({ ...prev, instanceState: true }));
            
            // Update last request
            setLastRequests(prev => ({ ...prev, instanceState: instanceGuid }));

            const data = { instanceGuid };
            const response = await postAuthenticatedRequest(
                "GetProcessInstanceStateCommand",
                data
            );

            if (response?.ok && response?.outData?.isSuccessful) {
                const result = response.outData.data;

                // Store directly in state
                setInstanceStates(prev => ({
                    ...prev,
                    [instanceGuid]: result
                }));

                return result;
            } else {
                throw new Error(response?.outData?.message ?? "Failed to fetch instance state");
            }
        } catch (error) {
            console.error('Error fetching instance state:', error);
            message.error(error.message);
            throw error;
        } finally {
            setLoading(prev => ({ ...prev, instanceState: false }));
        }
    }, [lastRequests, instanceStates]);

    // Get first active instance for a process
    const getFirstActiveInstance = useCallback(async (processId, forceRefresh = false) => {
        try {
            const result = await getProcessInstances(processId, 1, 1, forceRefresh);

            if (result?.instances && result.instances.length > 0) {
                const firstInstance = result.instances[0];

                // Get detailed state for the first instance
                if (firstInstance.instanceGuid) {
                    const state = await getProcessInstanceState(firstInstance.instanceGuid, forceRefresh);
                    return {
                        instance: firstInstance,
                        state
                    };
                }
            }

            return null;
        } catch (error) {
            console.error('Error getting first active instance:', error);
            return null;
        }
    }, [getProcessInstances, getProcessInstanceState]);

    // Get workflow details for entity
    const getWorkflowDetailsForEntity = useCallback(async (entityId, processId, instanceGuid, forceRefresh = false) => {
        try {
            let instanceState;

            // If instanceGuid is provided, use it directly
            if (instanceGuid) {
                instanceState = await getProcessInstanceState(instanceGuid, forceRefresh);
            }
            // Otherwise, try to find the first active instance for this entity
            else if (processId) {
                const firstInstance = await getFirstActiveInstance(processId, forceRefresh);
                if (firstInstance) {
                    instanceState = firstInstance.state;
                }
            }

            if (!instanceState) {
                return null;
            }

            // Extract workflow details
            return extractWorkflowDetails(instanceState, entityId);
        } catch (error) {
            console.error('Error getting workflow details for entity:', error);
            return null;
        }
    }, [getProcessInstanceState, getFirstActiveInstance]);

    // Extract workflow details from API response
    const extractWorkflowDetails = (apiResponse, entityId = null) => {
        if (!apiResponse?.state) {
            return null;
        }

        const stateData = apiResponse.state;

        try {
            // Parse the state JSON string
            const parsedState = typeof stateData === 'string'
                ? JSON.parse(stateData)
                : stateData;

            // Extract current task from logs
            const logs = apiResponse.logs || [];
            let currentTaskName = "Not Started";
            let currentTaskId = null;

            // Find the last waiting or executing task from logs
            for (let i = logs.length - 1; i >= 0; i--) {
                const log = logs[i];
                if (log.includes("[WAIT] Process paused at UserTask:") ||
                    log.includes("[USERTASK] Waiting for user action:") ||
                    log.includes("[EXEC] Executing:")) {
                    const parts = log.split(":");
                    if (parts.length > 1) {
                        const taskInfo = parts[1].trim();
                        const match = taskInfo.match(/\((.*?)\)/);
                        currentTaskName = match ? match[1] : taskInfo;

                        const idMatch = taskInfo.match(/^\s*(\S+)/);
                        currentTaskId = idMatch ? idMatch[1] : null;
                        break;
                    }
                }
            }

            // Extract variables
            const variables = parsedState.Variables || {};
            const currentUser = variables.currentUser || "system";

            // Get process name from XML
            const processDefinitionXml = apiResponse.processDefinitionXml || "";
            let processName = "Workflow Process";

            const nameMatch = processDefinitionXml.match(/name="([^"]+)"/);
            if (nameMatch && nameMatch[1]) {
                processName = nameMatch[1];
            }

            return {
                name: processName,
                state: apiResponse.processRunStatus || "Unknown",
                entityState: parsedState.Status === 0 ? "In Progress" : "Completed",
                entityStateReason: currentTaskName,
                holder: currentUser,
                product: "Entity Management",
                updatedAt: parsedState.StartTime ? new Date(parsedState.StartTime).toLocaleDateString() : "Never",
                updatedTime: parsedState.StartTime ? new Date(parsedState.StartTime).toLocaleTimeString() : "",
                billingStatus: "Active",
                instanceGuid: apiResponse.instanceGuid,
                currentTaskId,
                currentTaskName,
                startedAt: parsedState.StartTime ? new Date(parsedState.StartTime).toLocaleString() : "—",
                lastAction: currentTaskName,
                currentStep: currentTaskName,
                assignee: variables.responsibleUser || "—",
                dueDate: "—",
                instanceId: apiResponse.processInstanceId,
                processDefinitionId: apiResponse.processDefinitionId,
                status: apiResponse.status,
                entityId, // Include the entity ID if provided
            };
        } catch (error) {
            console.error('Error parsing workflow state:', error);
            return null;
        }
    };

    // Clear specific data
    const clearData = (dataType, key = null) => {
        switch (dataType) {
            case 'instances':
                if (key) {
                    setProcessInstances(prev => {
                        const newData = { ...prev };
                        delete newData[key];
                        return newData;
                    });
                } else {
                    setProcessInstances({});
                }
                break;
            case 'states':
                if (key) {
                    setInstanceStates(prev => {
                        const newData = { ...prev };
                        delete newData[key];
                        return newData;
                    });
                } else {
                    setInstanceStates({});
                }
                break;
            case 'lastRequests':
                setLastRequests({
                    instances: null,
                    instanceState: null
                });
                break;
            case 'all':
                setProcessInstances({});
                setInstanceStates({});
                setLastRequests({
                    instances: null,
                    instanceState: null
                });
                break;
        }
    };

    // Refresh specific data
    const refreshData = async (dataType, identifier) => {
        switch (dataType) {
            case 'instances':
                const [entityReferenceId, entityId, page, pageSize] = identifier.split('-');
                return await getProcessInstances(entityId, entityReferenceId, parseInt(page), parseInt(pageSize), true);
            case 'state':
                return await getProcessInstanceState(identifier, true);
            case 'entityWorkflow':
                const [entityIdParam, processIdParam, instanceGuidParam] = identifier.split('|');
                return await getWorkflowDetailsForEntity(entityIdParam, processIdParam, instanceGuidParam || null, true);
        }
    };

    const value = {
        // Data
        loading,
        processInstances,
        instanceStates,
        lastRequests,

        // Methods
        getProcessInstances,
        getProcessInstanceState,
        getFirstActiveInstance,
        getWorkflowDetailsForEntity,
        extractWorkflowDetails,

        // Data management
        clearData,
        refreshData,
    };

    return (
        <WorkflowContext.Provider value={value}>
            {children}
        </WorkflowContext.Provider>
    );
};

export const useWorkflow = () => {
    const context = useContext(WorkflowContext);
    if (!context) {
        throw new Error('useWorkflow must be used within a WorkflowProvider');
    }
    return context;
};